import {ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild} from "@angular/core";
import {Store} from "@ngrx/store";
import {
    selectFundWeightedCashflowBreakdown,
    selectSelectedFundCurrencyIsoCode,
    selectSelectedFundValuationCashFlowsForScenarioType,
    selectSelectedFundValuationId,
    selectSelectedFundValuationScenarios
} from "../../../store/fund-valuation/fund-valuation.selectors";
import {ScenarioType} from "../../../../shared/model/scenario-type";
import {combineLatest, filter, Observable, Subscription} from "rxjs";
import {AggregatedFundCashflows} from "../../../models/aggregated-fund-cashflows";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {selectFundContext} from "../../../store/fund/fund.selectors";
import {Router} from "@angular/router";
import {FundCashflow} from "../../../models/fund-cashflow";
import {DateTime} from "luxon";
import {AggregatedFundCashflowRecord} from "../../../models/aggregated-fund-cashflow-record";
import {take} from "rxjs/operators";
import {selectSensitivityDetailsCashflowForScnType} from "../../../fund-valuation-sensitivity-page/store/fv-sensitivity.selectors";
import {FundWeightedCashflowBreakdownActions} from "../../../store/fund.actions";

type BreakdownColumn = "low" | "base" | "high" | "weighted";

@Component({
    selector: "valumize-fund-cashflow-table",
    templateUrl: "./fund-cashflow-table.component.html",
    styleUrls: ["./fund-cashflow-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FundCashflowTableComponent implements OnInit, OnChanges, OnDestroy {
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly dialog = inject(MatDialog);

    @ViewChild("popupTemplate") popupTemplate!: TemplateRef<any>;

    @Input() scenarioType: ScenarioType = "BASE";
    @Input() displaySecondaryColumns = false;
    @Input() displayPatternColumns = false;
    @Input() displayHurdleColumns = false;
    @Input() isBottomUpValuation = false;
    @Input() isSensitivityView = false;

    dialogRef!: MatDialogRef<any>;
    subscriptions: Subscription[] = [];

    selectCashflowByScenarioType?: Observable<AggregatedFundCashflows | undefined>;

    readonly DIALOG_COLUMNS: BreakdownColumn[] = ["low", "base", "high", "weighted"];
    readonly DIALOG_COLUMN_LABELS: Record<BreakdownColumn, string> = {
        low: "LOW",
        base: "BASE",
        high: "HIGH",
        weighted: "Weighted"
    };
    selectedColumns: BreakdownColumn[] = [...this.DIALOG_COLUMNS];
    readonly currency$ = this.store.select(selectSelectedFundCurrencyIsoCode);

    onColumnPickerChange(columns: BreakdownColumn[]): void {
        this.selectedColumns = columns;
    }

    allColumns: string[] = [
        "date",
        "fundAgeInQuarters",
        "assetDistribution",
        "assetDistributionTopDownPattern",
        "assetDrawdown",
        "gpManagementFee",
        "gpManagementFeePattern",
        "gpUnfundedPayment",
        "gpUnfundedPaymentPattern",
        "gpUnfundedDistribution",
        "gpUnfundedDistributionPattern",
        "gpUnfundedNet",
        "purchasePrice",
        "gpGross",
        "gpCarryNet",
        "gpCarryPayment",
        "gpCarryPaymentHurdle",
        "secondaryCashflow",
        "secondaryManagementFee",
        "secondaryManagementFeePattern",
        "secondaryCarryNet",
        "secondaryCarryPayment",
        "secondaryCarryPaymentHurdle",
        "secondaryNetCashflow"
    ];

    displayColumns = this.allColumns;

    ngOnInit(): void {
        if (this.isSensitivityView) {
            this.selectCashflowByScenarioType = this.store.select(selectSensitivityDetailsCashflowForScnType(this.scenarioType));
        } else {
            this.selectCashflowByScenarioType = this.store.select(selectSelectedFundValuationCashFlowsForScenarioType(this.scenarioType));
        }
        if (this.isBottomUpValuation) {
            this.allColumns = this.allColumns.filter(c => c !== "assetDistributionTopDownPattern");
        }
        this.setDisplayedColumns();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            (changes["displaySecondaryColumns"] && !changes["displaySecondaryColumns"].firstChange) ||
            (changes["displayPatternColumns"] && !changes["displayPatternColumns"].firstChange) ||
            (changes["displayHurdleColumns"] && !changes["displayHurdleColumns"].firstChange)
        ) {
            this.setDisplayedColumns();
        }
    }

    setDisplayedColumns() {
        const hide = (c: string) =>
            (!this.displaySecondaryColumns && c.startsWith("secondary") && c !== "secondaryCashflow") ||
            (!this.displayPatternColumns && c.endsWith("Pattern")) ||
            (!this.displayHurdleColumns && c.endsWith("Hurdle"));

        this.displayColumns = this.allColumns.filter(c => !hide(c));
    }

    getTopDownPattern(element: AggregatedFundCashflowRecord): number | null {
        return (element.details.find((detail: FundCashflow) => detail.cashflowType.code === "DISTRIBUTION"))?.pattern.factor ?? null;
    }

    openPopup(element: AggregatedFundCashflowRecord, cashflowTypes: string[]): void {
        this.subscriptions.push(
            combineLatest([
                this.store.select(selectSelectedFundValuationId).pipe(take(1)),
                this.store.select(selectSelectedFundValuationScenarios).pipe(take(1))
            ]).subscribe(([fundValuationId, scenarios]) => {
                if (!fundValuationId) return;

                this.store.dispatch(FundWeightedCashflowBreakdownActions.load({
                    fundValuationId,
                    date: element.date,
                    cashflowTypes
                }));

                const lowProbability = scenarios.find(s => s.scenario.code === "LOW")?.probability?.fraction ?? null;
                const baseProbability = scenarios.find(s => s.scenario.code === "BASE")?.probability?.fraction ?? null;
                const highProbability = scenarios.find(s => s.scenario.code === "HIGH")?.probability?.fraction ?? null;

                this.subscriptions.push(
                    this.store.select(selectFundWeightedCashflowBreakdown).pipe(
                        filter(s => s.status === "LOADED" || s.status === "ERROR"),
                        take(1)
                    ).subscribe(state => {
                        if (state.status === "ERROR" || !state.data || state.data.assetBreakdowns.length === 0) return;

                        const formattedDate = DateTime.fromISO(element.date).toFormat("dd/MM/yyyy");
                        const titlePrefix = cashflowTypes.includes("EXIT") || cashflowTypes.includes("DISTRIBUTION")
                            ? "Asset Cashflow" : "Asset Drawdown";
                        const title = `${titlePrefix} for ${formattedDate}`;

                        this.dialogRef = this.dialog.open(this.popupTemplate, {
                            width: "900px",
                            maxWidth: "95vw",
                            data: {breakdown: state.data, title, lowProbability, baseProbability, highProbability}
                        });
                        this.subscriptions.push(
                            this.dialogRef.afterClosed().subscribe(() => {
                                this.selectedColumns = [...this.DIALOG_COLUMNS];
                            })
                        );
                    })
                );
            })
        );
    }

    navigateToAsset(detail: FundCashflow): void {
        this.navigateToAssetById(detail.originalAssetId);
    }

    navigateToAssetById(assetId: number | undefined): void {
        if (assetId == null) return;

        this.subscriptions.push(
            this.store.select(selectFundContext).pipe(take(1))
                .subscribe((ctx) => {
                    if (!ctx) return;
                    this.router.navigate(["../../assets/", assetId], {
                        queryParams: {
                            fundId: ctx.fundId,
                            fundReportId: ctx.fundReportId,
                            fundValuationId: ctx.fundValuationId,
                            dealId: ctx.dealId
                        }
                    });
                    this.dialogRef?.close();
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
